import { errorEnvelopeSchema, successEnvelope } from '@steado/admin-contract/envelope';
import { z } from 'zod';

import { ApiError, networkError } from './api-error';

/** Every route in this API lives under one prefix. */
export const API_PREFIX = '/api/v1';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions<TSchema extends z.ZodTypeAny> {
  readonly path: string;
  /** Schema for the envelope's `data`. Nothing is returned unparsed. */
  readonly schema: TSchema;
  readonly method?: HttpMethod;
  readonly query?: Readonly<Record<string, QueryValue>>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Skips the refresh-and-retry dance. Used by the refresh call itself. */
  readonly skipReauth?: boolean;
}

export interface Credentials {
  readonly accessToken: string;
  readonly refreshToken: string;
}

export interface ApiClientDeps {
  readonly baseUrl: string;
  readonly initialCredentials?: Credentials | null;
  /**
   * Exchanges a refresh token for a new pair, or returns null if the session is
   * over. Supplied by the auth layer, which owns the endpoint; the client owns
   * only *when* to call it.
   */
  readonly onRefresh?: (refreshToken: string) => Promise<Credentials | null>;
  /** Told about a rotation so the session can be persisted. */
  readonly onCredentialsChanged?: (credentials: Credentials | null) => void;
  readonly fetchImpl?: typeof fetch;
}

export interface ApiResult<T> {
  readonly data: T;
  /** Open by design — pagination lives here, and routes add their own keys. */
  readonly meta: Record<string, unknown>;
}

export interface ApiClient {
  request: <TSchema extends z.ZodTypeAny>(options: RequestOptions<TSchema>) => Promise<z.infer<TSchema>>;
  /** Same as `request`, but keeps the envelope's `meta` — needed for paging. */
  requestWithMeta: <TSchema extends z.ZodTypeAny>(
    options: RequestOptions<TSchema>,
  ) => Promise<ApiResult<z.infer<TSchema>>>;
  /**
   * A route that answers with bytes rather than an envelope: a private photo
   * the console shows on request. Through the client rather than an `<img
   * src>`, because only the client holds the token, and a URL that carried
   * one would end up in history and logs. Errors still arrive as envelopes.
   */
  requestBlob: (options: Omit<RequestOptions<z.ZodTypeAny>, 'schema' | 'body'>) => Promise<Blob>;
  /**
   * Pushed in by the auth layer on sign-in, sign-out and restore.
   *
   * The client holds its own credentials rather than reading them back out of
   * React on every call: a token has to be current at request time, and pulling
   * it through a ref would mean handing a ref to a non-React function.
   */
  setCredentials: (credentials: Credentials | null) => void;
}

export function createApiClient(deps: ApiClientDeps): ApiClient {
  const doFetch = deps.fetchImpl ?? globalThis.fetch.bind(globalThis);

  let credentials: Credentials | null = deps.initialCredentials ?? null;

  /**
   * One refresh at a time. Without this, a screen firing five queries at once
   * would answer a single expired token with five parallel refreshes, and the
   * rotation-reuse detection on the server would revoke the whole family.
   */
  let inFlightRefresh: Promise<string | null> | null = null;

  function refreshOnce(): Promise<string | null> {
    const current = credentials;
    if (deps.onRefresh === undefined || current === null) {
      return Promise.resolve(null);
    }

    inFlightRefresh ??= deps
      .onRefresh(current.refreshToken)
      .then((rotated) => {
        credentials = rotated;
        deps.onCredentialsChanged?.(rotated);
        return rotated?.accessToken ?? null;
      })
      .catch(() => {
        credentials = null;
        deps.onCredentialsChanged?.(null);
        return null;
      })
      .finally(() => {
        inFlightRefresh = null;
      });

    return inFlightRefresh;
  }

  async function send(options: RequestOptions<z.ZodTypeAny>, token: string | null): Promise<Response> {
    const url = new URL(`${API_PREFIX}${options.path}`, deps.baseUrl);

    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }

    /**
     * `FormData` is passed through untouched and, critically, without a
     * content-type header: the browser has to set one, because only it knows
     * the multipart boundary it is about to generate. Setting
     * `multipart/form-data` by hand produces a body the server cannot parse.
     */
    const isMultipart = options.body instanceof FormData;

    const headers: Record<string, string> = { accept: 'application/json' };
    if (options.body !== undefined && !isMultipart) headers['content-type'] = 'application/json';
    if (token !== null) headers.authorization = `Bearer ${token}`;

    try {
      return await doFetch(url, {
        method: options.method ?? 'GET',
        headers,
        ...(options.body === undefined
          ? {}
          : { body: isMultipart ? options.body : JSON.stringify(options.body) }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
    } catch (cause) {
      // An aborted request is the caller's own doing, not a transport failure.
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw networkError(cause);
    }
  }

  async function requestWithMeta<TSchema extends z.ZodTypeAny>(
    options: RequestOptions<TSchema>,
  ): Promise<ApiResult<z.infer<TSchema>>> {
    let response = await send(options, credentials?.accessToken ?? null);

    if (response.status === 401 && options.skipReauth !== true) {
      const refreshed = await refreshOnce();
      if (refreshed !== null) {
        response = await send(options, refreshed);
      }
    }

    return parse(response, options.schema);
  }

  async function request<TSchema extends z.ZodTypeAny>(
    options: RequestOptions<TSchema>,
  ): Promise<z.infer<TSchema>> {
    const result = await requestWithMeta(options);
    return result.data;
  }

  async function requestBlob(options: Omit<RequestOptions<z.ZodTypeAny>, 'schema' | 'body'>): Promise<Blob> {
    const sendable = { ...options, schema: z.unknown() };
    let response = await send(sendable, credentials?.accessToken ?? null);

    if (response.status === 401 && options.skipReauth !== true) {
      const refreshed = await refreshOnce();
      if (refreshed !== null) {
        response = await send(sendable, refreshed);
      }
    }

    if (response.ok) return response.blob();

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // Not an envelope; `toApiError` falls back to the status.
    }
    throw toApiError(response.status, payload);
  }

  function setCredentials(next: Credentials | null): void {
    credentials = next;
  }

  return { request, requestWithMeta, requestBlob, setCredentials };
}

async function parse<TSchema extends z.ZodTypeAny>(
  response: Response,
  schema: TSchema,
): Promise<ApiResult<z.infer<TSchema>>> {
  // 204 carries no body; the caller's schema should be z.null().
  if (response.status === 204) {
    return { data: schema.parse(null) as z.infer<TSchema>, meta: {} };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError({
      status: response.status,
      code: response.ok ? 'INTERNAL_ERROR' : mapStatus(response.status),
      message: `The API returned a ${String(response.status)} with a body that was not JSON.`,
    });
  }

  if (!response.ok) {
    throw toApiError(response.status, payload);
  }

  // Two steps rather than `successEnvelope(schema)`: the envelope is checked
  // once with a concrete type, then the payload against the caller's schema.
  // A combined generic loses `data`'s type, and this reports which of the two
  // actually failed.
  const envelope = successEnvelope(z.unknown()).safeParse(payload);
  if (!envelope.success) {
    throw protocolError(response.status, 'envelope', envelope.error);
  }

  const parsed = schema.safeParse(envelope.data.data);
  if (!parsed.success) {
    throw protocolError(response.status, 'payload', parsed.error);
  }

  return { data: parsed.data as z.infer<TSchema>, meta: envelope.data.meta ?? {} };
}

/** A 2xx whose shape we do not recognise is a protocol violation, not data. */
function protocolError(status: number, part: 'envelope' | 'payload', error: z.ZodError): ApiError {
  const issues = error.issues.map((issue) => `${issue.path.join('.') || part} ${issue.message}`).join('; ');

  return new ApiError({
    status,
    code: 'INTERNAL_ERROR',
    message: `The API returned a response the console does not understand: ${issues}`,
  });
}

function toApiError(status: number, payload: unknown): ApiError {
  const envelope = errorEnvelopeSchema.safeParse(payload);

  if (!envelope.success) {
    return new ApiError({
      status,
      code: mapStatus(status),
      message: `The API returned ${String(status)}.`,
    });
  }

  return new ApiError({
    status,
    code: envelope.data.error.code,
    message: envelope.data.error.message,
    requestId: envelope.data.error.requestId,
    details: envelope.data.error.details ?? [],
  });
}

/** Fallback when the body was not a recognisable error envelope. */
const CODE_BY_STATUS: Readonly<Record<number, string>> = {
  400: 'VALIDATION_ERROR',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  422: 'UNPROCESSABLE',
  429: 'RATE_LIMITED',
  503: 'UPSTREAM_UNAVAILABLE',
};

function mapStatus(status: number): string {
  return CODE_BY_STATUS[status] ?? 'INTERNAL_ERROR';
}
